import { ActionIcon, useMantineColorScheme } from '@mantine/core';
import { IconSun, IconMoon } from '@tabler/icons-react';
import React, { useState, useEffect } from "react";
import { apiService } from '../services/apiService';

export function ThemeToggle() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const [currentTheme, setCurrentTheme] = useState<string>(colorScheme);

  useEffect(() => {
    // Sync with colorScheme if it changes externally
    if (colorScheme && colorScheme !== currentTheme) {
      setCurrentTheme(colorScheme);
    }
  }, [colorScheme]);

  useEffect(() => {
    // Load theme from backend settings if user is logged in
    const loadUserTheme = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await apiService.getSettings();
          const settings = response.settings || {};
          if (settings.theme && settings.theme !== 'system') {
            setCurrentTheme(settings.theme);
            setColorScheme(settings.theme);
          }
        }
      } catch (error) {
        console.error('Failed to load theme setting:', error);
      }
    };
    
    loadUserTheme();
  }, []);

  const toggleColorScheme = async () => {
    const newColorScheme = dark ? 'light' : 'dark';
    setColorScheme(newColorScheme);
    setCurrentTheme(newColorScheme);
    
    // Dispatch custom event for other components to listen to
    window.dispatchEvent(new CustomEvent('theme-changed', { detail: { theme: newColorScheme } }));
    
    // Save to backend if user is logged in
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await apiService.getSettings();
        const currentSettings = response.settings || {};
        
        const updatedSettings = {
          ...currentSettings,
          theme: newColorScheme
        };
        
        await apiService.updateSettings(updatedSettings);
      } else {
        // Not logged in, just save to localStorage
        localStorage.setItem('mantine-color-scheme', newColorScheme);
      }
    } catch (error) {
      console.error('Failed to save theme setting:', error);
      // Still update UI even if backend save fails
      localStorage.setItem('mantine-color-scheme', newColorScheme);
    }
  };

  return (
    <ActionIcon
      variant="outline"
      color={dark ? 'yellow' : 'blue'}
      onClick={toggleColorScheme}
      title="Toggle color scheme"
      size="lg"
    >
      {dark ? <IconSun size={18} /> : <IconMoon size={18} />}
    </ActionIcon>
  );
}
